import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/* ──────────────────────────────────────────────────────────────────
 * Window Store — Desktop OS window manager state
 *
 * Tracks every open module window: position, size, z-index, focus,
 * minimized/maximized state, snap zones. Persists position + size
 * to localStorage so windows remember where you left them.
 *
 * Window state is INDEPENDENT of routing. Opening/closing windows
 * does NOT change the URL.
 * ────────────────────────────────────────────────────────────────── */

/**
 * Runtime list of every module key. The `ModuleKey` union is derived from it
 * so the two can never drift apart — anything reading a module key from an
 * untrusted source (a URL, localStorage) needs a runtime list to validate
 * against, and duplicating the union by hand is how those lists rot.
 */
export const MODULE_KEYS = [
  "dashboard",
  "crm",
  "pm",
  "people",
  "finance",
  "inventory",
  "support",
  "retainers",
  "chat",
  "blobby",
  "rewards",
  "qa",
  "settings",
  "email",
  // Platform operator console. Never appears on a rail (platformOnly in the
  // module registry) — it is opened from the superadmin menu entry, and its
  // content component re-checks the role so ?win=admin cannot be typed into.
  "admin",
  "trash",
] as const;

export type ModuleKey = (typeof MODULE_KEYS)[number];

export interface WindowRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type SnapZone =
  | "left"
  | "right"
  | "top"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | null;

export interface WindowState {
  moduleKey: ModuleKey;
  /** Display title used in the chrome. */
  title: string;
  /** Current rect (x, y from stage top-left; width/height in px). */
  rect: WindowRect;
  /** Rect saved before maximizing/snapping — used to restore. */
  savedRect: WindowRect | null;
  /** z-index stacking order; higher = on top. */
  z: number;
  isMinimized: boolean;
  isMaximized: boolean;
}

/* ──────────────────────────────────────────────────────────────────
 * Focus Mode — narrows attention to one window at a time.
 *
 * When active, all non-focused windows are visually dimmed and made
 * non-interactive. A FocusModeBar in the shell shows the task label
 * + elapsed time and an "End focus" affordance.
 *
 * startedAt is a Unix timestamp (ms) supplied by the caller so the
 * store stays pure (no Date.now() side-effects inside set()).
 * ────────────────────────────────────────────────────────────────── */
export interface FocusModeState {
  active: boolean;
  windowId: ModuleKey | null;
  taskLabel: string | null;
  startedAt: number | null;
}

interface WindowStoreState {
  /** Ordered from oldest to newest open. */
  windows: WindowState[];
  /** moduleKey of the currently focused window, or null if none. */
  focusedKey: ModuleKey | null;
  /** Next z-index to assign on focus. */
  nextZ: number;
  /** Counter for cascade positioning of new windows. */
  openCount: number;
  /** True once we've seeded the default dashboard window on first mount. */
  hasBootstrapped: boolean;
  /**
   * Currently hovered snap zone during a drag. null when not dragging
   * or when cursor is in the center of the stage. Used to render the
   * snap preview overlay.
   */
  snapHint: SnapZone;
  /** Focus mode state — narrows attention to one window. */
  focusMode: FocusModeState;
}

interface WindowStoreActions {
  /** Open a module window (or focus if already open). */
  openWindow: (input: {
    moduleKey: ModuleKey;
    title: string;
    defaultRect?: Partial<WindowRect> | undefined;
  }) => void;
  /** Close and remove a window from the stack. */
  closeWindow: (moduleKey: ModuleKey) => void;
  /** Bring a window to the front and mark it focused. */
  focusWindow: (moduleKey: ModuleKey) => void;
  /** Move a window (called during drag). */
  moveWindow: (moduleKey: ModuleKey, x: number, y: number) => void;
  /** Resize a window. */
  resizeWindow: (
    moduleKey: ModuleKey,
    width: number,
    height: number,
    x?: number,
    y?: number,
  ) => void;
  /** Toggle minimized state. */
  toggleMinimized: (moduleKey: ModuleKey) => void;
  /** Toggle maximized state. */
  toggleMaximized: (moduleKey: ModuleKey) => void;
  /**
   * Snap a window to a viewport zone (saving current rect for
   * restoration later). Caller provides the computed rect.
   */
  snapWindow: (moduleKey: ModuleKey, rect: WindowRect) => void;
  /** Unsnap — restore savedRect if present. */
  unsnapWindow: (moduleKey: ModuleKey) => void;
  /** Set the live snap hint during drag (for preview overlay). */
  setSnapHint: (zone: SnapZone) => void;
  /** Mark bootstrap complete after seeding the dashboard window. */
  markBootstrapped: () => void;
  /** Close all windows. */
  closeAll: () => void;
  /**
   * Enter focus mode for a window. Pass Date.now() as `startedAt` from the
   * calling component so the store remains free of side-effects.
   */
  enterFocusMode: (
    windowId: ModuleKey,
    startedAt: number,
    taskLabel?: string,
  ) => void;
  /** Exit focus mode and clear all focus state. */
  exitFocusMode: () => void;
}

type WindowStore = WindowStoreState & WindowStoreActions;

const INITIAL_WIDTH = 720;
const INITIAL_HEIGHT = 480;
const MIN_WIDTH = 320;
const MIN_HEIGHT = 220;
const CASCADE_STEP = 32;

function defaultRect(openCount: number): WindowRect {
  return {
    x: 80 + ((openCount * CASCADE_STEP) % 240),
    y: 60 + ((openCount * CASCADE_STEP) % 180),
    width: INITIAL_WIDTH,
    height: INITIAL_HEIGHT,
  };
}

export const useWindowStore = create<WindowStore>()(
  persist(
    (set, get) => ({
      windows: [],
      focusedKey: null,
      nextZ: 10,
      openCount: 0,
      hasBootstrapped: false,
      snapHint: null,
      focusMode: {
        active: false,
        windowId: null,
        taskLabel: null,
        startedAt: null,
      },

      openWindow: ({ moduleKey, title, defaultRect: inputRect }) => {
        const state = get();
        const existing = state.windows.find((w) => w.moduleKey === moduleKey);

        if (existing) {
          const newZ = state.nextZ + 1;
          set({
            windows: state.windows.map((w) =>
              w.moduleKey === moduleKey
                ? { ...w, z: newZ, isMinimized: false }
                : w,
            ),
            nextZ: newZ,
            focusedKey: moduleKey,
          });
          return;
        }

        const base = defaultRect(state.openCount);
        const rect: WindowRect = {
          x: inputRect?.x ?? base.x,
          y: inputRect?.y ?? base.y,
          width: inputRect?.width ?? base.width,
          height: inputRect?.height ?? base.height,
        };
        const newZ = state.nextZ + 1;
        const newWindow: WindowState = {
          moduleKey,
          title,
          rect,
          savedRect: null,
          z: newZ,
          isMinimized: false,
          isMaximized: false,
        };
        set({
          windows: [...state.windows, newWindow],
          nextZ: newZ,
          focusedKey: moduleKey,
          openCount: state.openCount + 1,
        });
      },

      closeWindow: (moduleKey) => {
        const state = get();
        const remaining = state.windows.filter(
          (w) => w.moduleKey !== moduleKey,
        );
        const nextFocused = remaining.reduce<ModuleKey | null>(
          (top, w) =>
            top === null ||
            (remaining.find((x) => x.moduleKey === top)?.z ?? 0) < w.z
              ? w.moduleKey
              : top,
          null,
        );
        set({ windows: remaining, focusedKey: nextFocused });
      },

      focusWindow: (moduleKey) => {
        const state = get();
        if (!state.windows.some((w) => w.moduleKey === moduleKey)) return;
        const newZ = state.nextZ + 1;
        set({
          windows: state.windows.map((w) =>
            w.moduleKey === moduleKey
              ? { ...w, z: newZ, isMinimized: false }
              : w,
          ),
          nextZ: newZ,
          focusedKey: moduleKey,
        });
      },

      moveWindow: (moduleKey, x, y) => {
        set((state) => ({
          windows: state.windows.map((w) =>
            w.moduleKey === moduleKey ? { ...w, rect: { ...w.rect, x, y } } : w,
          ),
        }));
      },

      resizeWindow: (moduleKey, width, height, x, y) => {
        set((state) => ({
          windows: state.windows.map((w) =>
            w.moduleKey === moduleKey
              ? {
                  ...w,
                  rect: {
                    x: x ?? w.rect.x,
                    y: y ?? w.rect.y,
                    width: Math.max(MIN_WIDTH, width),
                    height: Math.max(MIN_HEIGHT, height),
                  },
                }
              : w,
          ),
        }));
      },

      toggleMinimized: (moduleKey) => {
        set((state) => ({
          windows: state.windows.map((w) =>
            w.moduleKey === moduleKey
              ? { ...w, isMinimized: !w.isMinimized }
              : w,
          ),
          focusedKey: state.focusedKey === moduleKey ? null : state.focusedKey,
        }));
      },

      toggleMaximized: (moduleKey) => {
        set((state) => ({
          windows: state.windows.map((w) => {
            if (w.moduleKey !== moduleKey) return w;
            if (w.isMaximized && w.savedRect) {
              return {
                ...w,
                rect: w.savedRect,
                savedRect: null,
                isMaximized: false,
              };
            }
            return {
              ...w,
              savedRect: w.rect,
              isMaximized: true,
            };
          }),
        }));
      },

      snapWindow: (moduleKey, rect) => {
        set((state) => ({
          windows: state.windows.map((w) =>
            w.moduleKey === moduleKey
              ? {
                  ...w,
                  savedRect: w.savedRect ?? w.rect,
                  rect,
                  isMaximized: false,
                }
              : w,
          ),
        }));
      },

      unsnapWindow: (moduleKey) => {
        set((state) => ({
          windows: state.windows.map((w) =>
            w.moduleKey === moduleKey && w.savedRect
              ? { ...w, rect: w.savedRect, savedRect: null }
              : w,
          ),
        }));
      },

      setSnapHint: (zone) => {
        set({ snapHint: zone });
      },

      markBootstrapped: () => {
        set({ hasBootstrapped: true });
      },

      closeAll: () => {
        set({ windows: [], focusedKey: null });
      },

      enterFocusMode: (windowId, startedAt, taskLabel) => {
        const state = get();
        // Bring the target window to front as well
        const newZ = state.nextZ + 1;
        set({
          windows: state.windows.map((w) =>
            w.moduleKey === windowId
              ? { ...w, z: newZ, isMinimized: false }
              : w,
          ),
          nextZ: newZ,
          focusedKey: windowId,
          focusMode: {
            active: true,
            windowId,
            ...(taskLabel !== undefined ? { taskLabel } : { taskLabel: null }),
            startedAt,
          },
        });
      },

      exitFocusMode: () => {
        set({
          focusMode: {
            active: false,
            windowId: null,
            taskLabel: null,
            startedAt: null,
          },
        });
      },
    }),
    {
      name: "teamblob.windows",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        windows: state.windows.map((w) => ({
          moduleKey: w.moduleKey,
          title: w.title,
          rect: w.rect,
          savedRect: w.savedRect,
          z: 10,
          isMinimized: w.isMinimized,
          isMaximized: w.isMaximized,
        })),
        openCount: state.openCount,
        hasBootstrapped: state.hasBootstrapped,
      }),
    },
  ),
);

export const useOpenWindows = () => useWindowStore((s) => s.windows);
export const useFocusedKey = () => useWindowStore((s) => s.focusedKey);
export const useFocusMode = () => useWindowStore((s) => s.focusMode);
